import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import type { useRouter } from 'next/router'

import type { PlaybookStore } from './PlaybookStore'
import analyticsService from '@lib/analytics/AnalyticsService'
import type { SessionStore } from '@lib/store/session'

export abstract class BaseViewModel {
    // Common loading and error states
    loading = false
    isSubmitting = false
    errorMessage = ''

    // Navigation state
    protected router: ReturnType<typeof useRouter>
    protected searchParams: URLSearchParams
    protected playbook: PlaybookStore
    protected sessionStore: SessionStore

    protected constructor(
        router: ReturnType<typeof useRouter>,
        searchParams: URLSearchParams,
        playbook: PlaybookStore,
        sessionStore: SessionStore,
    ) {
        this.router = router
        this.searchParams = searchParams
        this.playbook = playbook
        this.sessionStore = sessionStore

        makeObservable(this, {
            loading: observable,
            isSubmitting: observable,
            errorMessage: observable,
            setLoading: action,
            setSubmitting: action,
            setError: action,
            clearError: action,
            hasError: computed,
        })
    }

    // Common computed properties
    get hasError(): boolean {
        return this.errorMessage !== ''
    }

    // Common actions for loading and error states
    setLoading(loading: boolean) {
        this.loading = loading
    }

    setSubmitting(submitting: boolean) {
        this.isSubmitting = submitting
    }

    setError(error: string) {
        this.errorMessage = error
    }

    clearError() {
        this.errorMessage = ''
    }

    // Optional cleanup method
    dispose?(): void

    // Navigation utilities
    protected async navigate(url: string, options?: { scroll?: boolean }) {
        await this.router.replace(url, undefined, options)
    }

    protected async updateQueryParam(key: string, value: string) {
        const currentParams = new URLSearchParams(this.searchParams.toString())
        currentParams.set(key, value)
        await this.router.replace(`?${currentParams.toString()}`, undefined, {
            scroll: false,
        })
    }

    protected getQueryParam(key: string): string | null {
        return this.searchParams.get(key)
    }

    // Playbook utilities
    protected setHeaderText(text: string) {
        this.playbook.setHeaderText(text)
    }

    protected setHeaderBackButtonVisible(visible: boolean) {
        this.playbook.setHeaderBackButtonVisible(visible)
    }

    protected setBackButtonFunction(fn: () => Promise<void>) {
        this.playbook.setBackButtonFn(fn)
    }

    // Analytics utilities
    protected async trackEvent(
        eventName: string,
        properties?: Record<string, any>,
    ) {
        await analyticsService.trackEvent(eventName, properties)
    }

    // Error handling utilities
    protected handleError(error: unknown, context?: string): void {
        const errorMessage =
            error instanceof Error ? error.message : 'An unknown error occurred'
        const contextualMessage = context
            ? `${context}: ${errorMessage}`
            : errorMessage

        runInAction(() => {
            this.setError(contextualMessage)
            this.setLoading(false)
            this.setSubmitting(false)
        })

        console.error(contextualMessage, error)
    }

    protected async executeWithErrorHandling<T>(
        operation: () => Promise<T>,
        context?: string,
        loadingState: 'loading' | 'submitting' = 'loading',
    ): Promise<T | null> {
        try {
            this.clearError()
            if (loadingState === 'loading') {
                this.setLoading(true)
            } else {
                this.setSubmitting(true)
            }

            const result = await operation()

            runInAction(() => {
                if (loadingState === 'loading') {
                    this.setLoading(false)
                } else {
                    this.setSubmitting(false)
                }
            })

            return result
        } catch (error) {
            this.handleError(error, context)
            return null
        }
    }
}
